<template>
  <div class="service-prospeccao">
    <p class="text-2xl mb-1">
      Prospecção
    </p>
    <p class="mb-4 grey--text text--darken-1">
      Configure filtros em etapas e acompanhe suas prospecções PF e PJ.
    </p>

    <v-card>
      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="primary"
      >
        <v-tab>Cadastrar prospecção</v-tab>
        <v-tab>Lista</v-tab>
      </v-tabs>

      <v-tabs-items
        v-model="activeTab"
        class="prospeccao-tabs-items"
      >
        <!-- Cadastrar (wizard) -->
        <v-tab-item>
          <v-card-text class="pt-5">
            <div class="d-flex flex-wrap align-center justify-space-between mb-2">
              <div class="d-flex align-center mb-2 mb-sm-0">
                <v-avatar
                  size="28"
                  class="me-2 prosp-avatar"
                >
                  <span class="text-caption font-weight-bold">
                    {{ currentStep }}
                  </span>
                </v-avatar>
                <span class="grey--text text--darken-1">
                  Etapa {{ currentStep }} de {{ totalSteps }}
                  <template v-if="tipo">
                    — {{ tipo === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica' }}
                  </template>
                </span>
              </div>

              <v-btn
                v-if="currentStep > 1"
                color="error"
                text
                small
                @click="reiniciarWizard"
              >
                Reiniciar
              </v-btn>
            </div>

            <div class="d-flex align-center mb-6">
              <v-progress-linear
                :value="progressPercent"
                color="primary"
                height="6"
                rounded
                class="me-3"
              />
              <span class="grey--text text-caption text-no-wrap">
                {{ progressPercent }}%
              </span>
            </div>

            <!-- Step 1: Perfil -->
            <template v-if="currentStep === 1">
              <v-card
                outlined
                class="mb-4"
              >
                <v-list-item class="py-3">
                  <v-list-item-content>
                    <v-list-item-title class="primary--text font-weight-bold text-wrap">
                      Defina o perfil da prospecção
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-wrap">
                      Escolha PF ou PJ, os canais de retorno e o nome
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-card>

              <p class="font-weight-medium mb-2">
                Tipo
              </p>
              <v-row dense class="mb-4">
                <v-col
                  v-for="option in tipoOptions"
                  :key="option.value"
                  cols="12"
                  sm="6"
                >
                  <v-card
                    outlined
                    class="option-card"
                    :class="{ 'option-card--selected': tipo === option.value }"
                    @click="tipo = option.value"
                  >
                    <v-list-item class="py-3">
                      <v-list-item-avatar
                        rounded
                        :class="tipo === option.value ? 'prosp-avatar--selected' : 'prosp-avatar'"
                      >
                        <v-icon>{{ option.icon }}</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold d-flex align-center">
                          {{ option.title }}
                          <v-chip
                            class="ms-2 prosp-tipo-chip"
                            :class="option.value === 'pj' ? 'prosp-tipo-chip--pj' : 'prosp-tipo-chip--pf'"
                            x-small
                            label
                            outlined
                          >
                            {{ option.chip }}
                          </v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ option.subtitle }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-card>
                </v-col>
              </v-row>

              <p class="font-weight-medium mb-2">
                Canais de retorno
              </p>
              <v-row dense class="mb-4">
                <v-col
                  v-for="canal in canalOptions"
                  :key="canal.value"
                  cols="12"
                  sm="4"
                >
                  <v-card
                    outlined
                    class="option-card"
                    :class="{ 'option-card--selected': canais.includes(canal.value) }"
                    @click="toggleCanal(canal.value)"
                  >
                    <v-list-item class="py-3">
                      <v-list-item-action class="me-2">
                        <v-checkbox
                          :input-value="canais.includes(canal.value)"
                          color="primary"
                          hide-details
                          dense
                          class="mt-0"
                          @click.stop="toggleCanal(canal.value)"
                        />
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">
                          {{ canal.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-wrap">
                          {{ canal.subtitle }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-card>
                </v-col>
              </v-row>

              <v-text-field
                v-model="nome"
                label="Nome da prospecção"
                outlined
                dense
                hide-details
                class="mb-2"
              />
            </template>

            <!-- Step 2: Localização (PF e PJ) -->
            <template v-else-if="currentStep === 2">
              <v-card
                outlined
                class="mb-4"
              >
                <v-list-item class="py-3">
                  <v-list-item-content>
                    <v-list-item-title class="primary--text font-weight-bold">
                      Localização
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      Selecione estados e/ou cidades da prospecção
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-card>

              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-autocomplete
                    v-model="selectedEstados"
                    :items="estadoOptions"
                    item-text="text"
                    item-value="value"
                    label="Estados"
                    outlined
                    dense
                    hide-details
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    @change="onEstadosChange"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-autocomplete
                    v-model="selectedCidades"
                    :items="cidadeOptionsFiltradas"
                    item-text="cidade"
                    item-value="codigo"
                    label="Cidades"
                    outlined
                    dense
                    hide-details
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    :loading="loadingCidades"
                    :disabled="loadingCidades"
                    :filter="filterCidades"
                  />
                </v-col>
              </v-row>
              <p class="caption grey--text mt-3 mb-0">
                Se informar cidades, elas têm prioridade no filtro. Sem cidades, usa apenas os estados selecionados.
              </p>
            </template>

            <!-- PF Step 3: Gênero e idade -->
            <template v-else-if="tipo === 'pf' && currentStep === 3">
              <v-card
                outlined
                class="mb-4"
              >
                <v-list-item class="py-3">
                  <v-list-item-content>
                    <v-list-item-title class="primary--text font-weight-bold">
                      Gênero e faixa etária
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-card>

              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-select
                    v-model="form.genero"
                    :items="generoOptions"
                    label="Gênero"
                    outlined
                    dense
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col
                  cols="6"
                  md="4"
                >
                  <v-text-field
                    v-model.number="form.faixaetariaini"
                    label="Idade inicial"
                    type="number"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="6"
                  md="4"
                >
                  <v-text-field
                    v-model.number="form.faixaetariafim"
                    label="Idade final"
                    type="number"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>

            <!-- PF Step 4: CBO e renda -->
            <template v-else-if="tipo === 'pf' && currentStep === 4">
              <v-card
                outlined
                class="mb-4"
              >
                <v-list-item class="py-3">
                  <v-list-item-content>
                    <v-list-item-title class="primary--text font-weight-bold">
                      CBO e renda
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      Selecione ocupações (CBO) e informe a faixa de renda
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-card>

              <v-row>
                <v-col cols="12">
                  <div class="d-flex flex-wrap mb-3">
                    <v-btn
                      small
                      outlined
                      color="success"
                      class="me-2 mb-2"
                      @click="selecionarTodosCbos"
                    >
                      Selecionar todos
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="error"
                      class="mb-2"
                      @click="selectedCbos = []"
                    >
                      Remover todos
                    </v-btn>
                  </div>
                  <v-autocomplete
                    v-model="selectedCbos"
                    :items="cboOptions"
                    item-text="label"
                    item-value="CodCbo"
                    label="CBOs — Classificação Brasileira de Ocupações"
                    placeholder="Buscar profissão ou código CBO"
                    outlined
                    dense
                    hide-details
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    :loading="loadingCbos"
                    :disabled="loadingCbos || cboTodosSelecionado"
                    :filter="filterCbos"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="form.rendaInicial"
                    label="Renda inicial"
                    prefix="R$"
                    outlined
                    dense
                    hide-details
                    placeholder="0,00"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="form.rendaFinal"
                    label="Renda final"
                    prefix="R$"
                    outlined
                    dense
                    hide-details
                    placeholder="0,00"
                  />
                </v-col>
              </v-row>
            </template>

            <!-- PJ Step 3: Empresa -->
            <template v-else-if="tipo === 'pj' && currentStep === 3">
              <v-card
                outlined
                class="mb-4"
              >
                <v-list-item class="py-3">
                  <v-list-item-content>
                    <v-list-item-title class="primary--text font-weight-bold">
                      Dados da empresa
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      CNAE, porte, natureza e situação
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-card>

              <v-row>
                <v-col cols="12">
                  <div class="d-flex flex-wrap mb-3">
                    <v-btn
                      small
                      outlined
                      color="success"
                      class="me-2 mb-2"
                      @click="selecionarTodosCnaes"
                    >
                      Selecionar todos
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="error"
                      class="mb-2"
                      @click="selectedCnaes = []"
                    >
                      Remover todos
                    </v-btn>
                  </div>
                  <v-autocomplete
                    v-model="selectedCnaes"
                    :items="cnaeOptions"
                    item-text="label"
                    item-value="idcnae"
                    label="CNAEs"
                    placeholder="Buscar CNAE"
                    outlined
                    dense
                    hide-details
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    :loading="loadingCnaes"
                    :disabled="loadingCnaes || cnaeTodosSelecionado"
                    :filter="filterCnaes"
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="4"
                >
                  <div class="d-flex flex-wrap mb-3">
                    <v-btn
                      small
                      outlined
                      color="success"
                      class="me-2 mb-2"
                      @click="selecionarTodosPortes"
                    >
                      Todos portes
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="error"
                      class="mb-2"
                      @click="selectedPortes = []"
                    >
                      Limpar
                    </v-btn>
                  </div>
                  <v-autocomplete
                    v-model="selectedPortes"
                    :items="porteOptions"
                    item-text="label"
                    item-value="IdPorteEmpresa"
                    label="Porte da empresa"
                    placeholder="Buscar porte"
                    outlined
                    dense
                    hide-details
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    :loading="loadingPortes"
                    :disabled="loadingPortes || porteTodosSelecionado"
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="4"
                >
                  <div class="d-flex flex-wrap mb-3">
                    <v-btn
                      small
                      outlined
                      color="success"
                      class="me-2 mb-2"
                      @click="selecionarTodasNaturezas"
                    >
                      Todas naturezas
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="error"
                      class="mb-2"
                      @click="selectedNaturezas = []"
                    >
                      Limpar
                    </v-btn>
                  </div>
                  <v-autocomplete
                    v-model="selectedNaturezas"
                    :items="naturezaOptions"
                    item-text="label"
                    item-value="IdNaturezaJuridica"
                    label="Natureza jurídica"
                    placeholder="Buscar natureza"
                    outlined
                    dense
                    hide-details
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    :loading="loadingNaturezas"
                    :disabled="loadingNaturezas || naturezaTodosSelecionado"
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="4"
                >
                  <div class="d-flex flex-wrap mb-3">
                    <v-btn
                      small
                      outlined
                      color="success"
                      class="me-2 mb-2"
                      @click="selecionarTodasSituacoes"
                    >
                      Todas situações
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="error"
                      class="mb-2"
                      @click="selectedSituacoes = []"
                    >
                      Limpar
                    </v-btn>
                  </div>
                  <v-autocomplete
                    v-model="selectedSituacoes"
                    :items="situacaoOptions"
                    item-text="label"
                    item-value="IdSituacaoEmpresa"
                    label="Situação da empresa"
                    placeholder="Buscar situação"
                    outlined
                    dense
                    hide-details
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    :loading="loadingSituacoes"
                    :disabled="loadingSituacoes || situacaoTodosSelecionado"
                  />
                </v-col>
              </v-row>
            </template>

            <!-- PJ Step 4: Porte financeiro -->
            <template v-else-if="tipo === 'pj' && currentStep === 4">
              <v-card
                outlined
                class="mb-4"
              >
                <v-list-item class="py-3">
                  <v-list-item-content>
                    <v-list-item-title class="primary--text font-weight-bold">
                      Funcionários, faturamento e tempo
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-card>

              <v-row>
                <v-col
                  cols="6"
                  md="3"
                >
                  <v-text-field
                    v-model="form.qtdFuncionariosInicial"
                    label="Funcionários ini."
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="6"
                  md="3"
                >
                  <v-text-field
                    v-model="form.qtdFuncionariosFinal"
                    label="Funcionários fim"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="6"
                  md="3"
                >
                  <v-text-field
                    v-model="form.faturamentoInicial"
                    label="Faturamento ini."
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="6"
                  md="3"
                >
                  <v-text-field
                    v-model="form.faturamentoFinal"
                    label="Faturamento fim"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="6"
                  md="3"
                >
                  <v-text-field
                    v-model="form.tempoEmpresaEmMesesIni"
                    label="Tempo (meses) ini."
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="6"
                  md="3"
                >
                  <v-text-field
                    v-model="form.tempoEmpresaEmMesesFim"
                    label="Tempo (meses) fim"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>

            <!-- PJ Step 5: Sócios -->
            <template v-else-if="tipo === 'pj' && currentStep === 5">
              <v-card
                outlined
                class="mb-4"
              >
                <v-list-item class="py-3">
                  <v-list-item-content>
                    <v-list-item-title class="primary--text font-weight-bold">
                      Sócios e capital social
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-card>

              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-switch
                    v-model="form.comsocios"
                    label="Incluir sócios"
                    dense
                    hide-details
                    class="mt-0"
                    :true-value="1"
                    :false-value="0"
                  />
                </v-col>
                <v-col
                  cols="6"
                  md="4"
                >
                  <v-text-field
                    v-model="form.capitalSocialInicial"
                    label="Capital social ini."
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="6"
                  md="4"
                >
                  <v-text-field
                    v-model="form.capitalSocialFinal"
                    label="Capital social fim"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>

            <!-- Review (última etapa) -->
            <template v-else-if="isReviewStep">
              <v-card
                outlined
                class="mb-4"
              >
                <v-list-item class="py-3">
                  <v-list-item-content>
                    <v-list-item-title class="primary--text font-weight-bold">
                      Revisar e enviar
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      Confira o resumo antes de criar a prospecção
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-card>

              <v-simple-table dense>
                <tbody>
                  <tr
                    v-for="row in reviewRows"
                    :key="row.label"
                  >
                    <td class="font-weight-medium">
                      {{ row.label }}
                    </td>
                    <td>{{ row.value }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </template>

            <div class="d-flex justify-space-between flex-wrap mt-6">
              <v-btn
                text
                color="grey darken-1"
                :disabled="currentStep === 1"
                @click="prevStep"
              >
                <v-icon left>
                  {{ icons.mdiArrowLeft }}
                </v-icon>
                Voltar
              </v-btn>

              <v-btn
                v-if="!isReviewStep"
                color="primary"
                @click="nextStep"
              >
                Continuar
                <v-icon right>
                  {{ icons.mdiArrowRight }}
                </v-icon>
              </v-btn>

              <v-btn
                v-else
                color="primary"
                :loading="sending"
                @click="enviar"
              >
                Enviar prospecção
              </v-btn>
            </div>
          </v-card-text>
        </v-tab-item>

        <!-- Lista -->
        <v-tab-item>
          <v-card-title class="d-flex align-center justify-space-between flex-wrap px-4 pt-6 pb-2">
            <div class="d-flex align-center flex-wrap">
              <span class="me-4">Prospecções</span>
              <v-btn-toggle
                v-model="listaTipo"
                dense
                mandatory
                class="me-2"
              >
                <v-btn
                  small
                  value="pf"
                >
                  PF
                </v-btn>
                <v-btn
                  small
                  value="pj"
                >
                  PJ
                </v-btn>
              </v-btn-toggle>
            </div>
            <div class="d-flex align-center mt-2 mt-sm-0">
              <v-text-field
                v-model.number="ultimosDias"
                label="Últimos dias"
                type="number"
                outlined
                dense
                hide-details
                style="max-width: 140px"
                class="me-2"
                min="1"
              />
              <v-btn
                color="primary"
                outlined
                :loading="loadingLista"
                @click="loadLista"
              >
                <v-icon
                  left
                  small
                >
                  {{ icons.mdiRefresh }}
                </v-icon>
                Atualizar
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-data-table
              :headers="listaHeaders"
              :items="lista"
              :loading="loadingLista"
              :items-per-page="10"
              class="elevation-0 mt-2"
              dense
              no-data-text="Nenhuma prospecção encontrada"
            >
              <template #item.actions="{ item }">
                <v-btn
                  small
                  text
                  color="primary"
                  :loading="actionId === item.id && actionType === 'contagem'"
                  @click="verContagem(item)"
                >
                  Contagem
                </v-btn>
                <v-btn
                  small
                  text
                  color="success"
                  @click="openAprovar(item)"
                >
                  Aprovar
                </v-btn>
                <v-btn
                  small
                  text
                  color="error"
                  :loading="actionId === item.id && actionType === 'recusar'"
                  @click="recusar(item)"
                >
                  Recusar
                </v-btn>
                <v-btn
                  small
                  text
                  :loading="actionId === item.id && actionType === 'retorno'"
                  @click="verRetorno(item)"
                >
                  Retorno
                </v-btn>
                <v-btn
                  small
                  text
                  :loading="actionId === item.id && actionType === 'download'"
                  @click="verDownload(item)"
                >
                  Download
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog
      v-model="detailDialog"
      max-width="760"
    >
      <v-card>
        <v-card-title>{{ detailTitle }}</v-card-title>
        <v-card-text>
          <pre
            class="pa-3 grey lighten-4 rounded"
            style="max-height: 420px; overflow: auto; white-space: pre-wrap;"
          >{{ JSON.stringify(detailData, null, 2) }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="detailDialog = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="aprovarDialog"
      max-width="420"
    >
      <v-card>
        <v-card-title>Aprovar prospecção</v-card-title>
        <v-card-text>
          <p class="mb-3">
            ID: <strong>{{ aprovarItem && aprovarItem.id }}</strong>
          </p>
          <v-text-field
            v-model.number="qtdAprovado"
            label="Quantidade a aprovar"
            type="number"
            outlined
            dense
            hide-details
            min="1"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="aprovarDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="success"
            :loading="actionType === 'aprovar'"
            @click="confirmarAprovar"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiRefresh,
  mdiArrowLeft,
  mdiArrowRight,
  mdiAccount,
  mdiDomain,
} from '@mdi/js'
import {
  enviarProspeccaoPf,
  listarProspeccoesPf,
  contagemProspeccaoPf,
  aprovarProspeccaoPf,
  recusarProspeccaoPf,
  retornoProspeccaoPf,
  enviarProspeccaoPj,
  listarProspeccoesPj,
  contagemProspeccaoPj,
  aprovarProspeccaoPj,
  recusarProspeccaoPj,
  retornoProspeccaoPj,
  downloadProspeccao,
  getCidadesProspeccao,
  getCbosProspeccao,
  getCnaesProspeccao,
  getPortesProspeccao,
  getNaturezasJuridicasProspeccao,
  getSituacoesEmpresaProspeccao,
} from '@/services/prospeccao'

const emptyForm = () => ({
  faixaetariaini: null,
  faixaetariafim: null,
  genero: null,
  rendaInicial: '',
  rendaFinal: '',
  tipoempresa: '',
  comsocios: 0,
  qtdFuncionariosInicial: '',
  qtdFuncionariosFinal: '',
  faturamentoInicial: '',
  faturamentoFinal: '',
  tempoEmpresaEmMesesIni: '',
  tempoEmpresaEmMesesFim: '',
  capitalSocialInicial: '',
  capitalSocialFinal: '',
})

const ESTADO_OPTIONS = [
  { text: 'Acre (AC)', value: 'AC' },
  { text: 'Alagoas (AL)', value: 'AL' },
  { text: 'Amapá (AP)', value: 'AP' },
  { text: 'Amazonas (AM)', value: 'AM' },
  { text: 'Bahia (BA)', value: 'BA' },
  { text: 'Ceará (CE)', value: 'CE' },
  { text: 'Distrito Federal (DF)', value: 'DF' },
  { text: 'Espírito Santo (ES)', value: 'ES' },
  { text: 'Goiás (GO)', value: 'GO' },
  { text: 'Maranhão (MA)', value: 'MA' },
  { text: 'Mato Grosso (MT)', value: 'MT' },
  { text: 'Mato Grosso do Sul (MS)', value: 'MS' },
  { text: 'Minas Gerais (MG)', value: 'MG' },
  { text: 'Pará (PA)', value: 'PA' },
  { text: 'Paraíba (PB)', value: 'PB' },
  { text: 'Paraná (PR)', value: 'PR' },
  { text: 'Pernambuco (PE)', value: 'PE' },
  { text: 'Piauí (PI)', value: 'PI' },
  { text: 'Rio de Janeiro (RJ)', value: 'RJ' },
  { text: 'Rio Grande do Norte (RN)', value: 'RN' },
  { text: 'Rio Grande do Sul (RS)', value: 'RS' },
  { text: 'Rondônia (RO)', value: 'RO' },
  { text: 'Roraima (RR)', value: 'RR' },
  { text: 'Santa Catarina (SC)', value: 'SC' },
  { text: 'São Paulo (SP)', value: 'SP' },
  { text: 'Sergipe (SE)', value: 'SE' },
  { text: 'Tocantins (TO)', value: 'TO' },
]

function normalizeLista(data) {
  const list =
    data?.results ||
    data?.prospeccoes ||
    data?.listarprospecaoPJ ||
    data?.listarprospecaoPF ||
    (Array.isArray(data) ? data : [])

  if (!Array.isArray(list)) return []

  return list.map(item => ({
    id:
      item.IdProspeccao ||
      item.IdProspecao ||
      item.idProspeccao ||
      item.id ||
      item.idlote,
    nome:
      item.NomeProspecao ||
      item.NomeProspeccao ||
      item.nomeprospecao ||
      item.nome ||
      '—',
    status:
      item.DescricaoStatus ||
      item.Status ||
      item.status ||
      item.IdStatus ||
      '—',
    total:
      item.TotalRegistros ||
      item.Quantidade ||
      item.qtd ||
      item.Total ||
      '—',
    data: item.DataInclusao || item.Data || item.data || '—',
    raw: item,
  }))
}

export default {
  data() {
    return {
      icons: {
        mdiRefresh,
        mdiArrowLeft,
        mdiArrowRight,
        mdiAccount,
        mdiDomain,
      },
      activeTab: 0,
      currentStep: 1,
      tipo: 'pf',
      nome: '',
      canais: ['celular'],
      form: emptyForm(),
      selectedEstados: [],
      selectedCidades: [],
      selectedCbos: [],
      selectedCnaes: [],
      selectedPortes: [],
      selectedNaturezas: [],
      selectedSituacoes: [],
      cidadesRaw: [],
      cbosRaw: [],
      cnaesRaw: [],
      portesRaw: [],
      naturezasRaw: [],
      situacoesRaw: [],
      loadingCidades: false,
      loadingCbos: false,
      loadingCnaes: false,
      loadingPortes: false,
      loadingNaturezas: false,
      loadingSituacoes: false,
      estadoOptions: ESTADO_OPTIONS,
      sending: false,
      listaTipo: 'pf',
      ultimosDias: 7,
      lista: [],
      loadingLista: false,
      actionId: null,
      actionType: '',
      detailDialog: false,
      detailTitle: '',
      detailData: null,
      aprovarDialog: false,
      aprovarItem: null,
      qtdAprovado: 1,
      tipoOptions: [
        {
          value: 'pf',
          title: 'Consumidores',
          subtitle: 'Prospecção com base em Pessoa Física',
          chip: 'PF',
          icon: mdiAccount,
        },
        {
          value: 'pj',
          title: 'Empresas',
          subtitle: 'Prospecção com base em Pessoa Jurídica',
          chip: 'PJ',
          icon: mdiDomain,
        },
      ],
      canalOptions: [
        {
          value: 'email',
          title: 'E-mail',
          subtitle: 'Retorno com pelo menos 1 e-mail',
        },
        {
          value: 'celular',
          title: 'Celular',
          subtitle: 'Retorno com pelo menos 1 celular',
        },
        {
          value: 'fixo',
          title: 'Fixo',
          subtitle: 'Retorno com pelo menos 1 fixo',
        },
      ],
      generoOptions: [
        { text: 'Masculino', value: 'M' },
        { text: 'Feminino', value: 'F' },
        { text: 'Todos', value: '' },
      ],
      listaHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Nome', value: 'nome' },
        { text: 'Status', value: 'status' },
        { text: 'Total', value: 'total' },
        { text: 'Data', value: 'data' },
        { text: 'Ações', value: 'actions', sortable: false },
      ],
    }
  },

  computed: {
    totalSteps() {
      return this.tipo === 'pj' ? 6 : 5
    },

    progressPercent() {
      return Math.round((this.currentStep / this.totalSteps) * 100)
    },

    isReviewStep() {
      return this.currentStep === this.totalSteps
    },

    cidadeOptionsFiltradas() {
      const list = this.cidadesRaw || []
      if (!this.selectedEstados.length) return list

      const ufs = this.selectedEstados.map(uf => String(uf).toUpperCase())
      return list.filter(item => {
        const label = String(item.cidade || '')
        const ufMatch = label.match(/-\s*([A-Z]{2})\s*$/i)
        const uf = ufMatch ? ufMatch[1].toUpperCase() : ''
        return ufs.includes(uf)
      })
    },

    cidadesSelecionadasLabels() {
      if (!this.selectedCidades.length) return []
      const map = new Map(
        (this.cidadesRaw || []).map(item => [String(item.codigo), item.cidade]),
      )
      return this.selectedCidades.map(codigo => map.get(String(codigo)) || codigo)
    },

    cboOptions() {
      return (this.cbosRaw || []).map(item => ({
        ...item,
        CodCbo: String(item.CodCbo ?? item.codCbo ?? item.codigo ?? ''),
        CBO: String(item.CBO ?? item.cbo ?? item.Descricao ?? item.descricao ?? ''),
        label: `${item.CodCbo ?? item.codCbo ?? ''} — ${item.CBO ?? item.cbo ?? item.Descricao ?? ''}`.trim(),
      })).filter(item => item.CodCbo)
    },

    cboTodosSelecionado() {
      return this.selectedCbos.length === 1 && String(this.selectedCbos[0]) === 'Todos'
    },

    cbosSelecionadosLabels() {
      if (this.cboTodosSelecionado) return ['Todos']
      if (!this.selectedCbos.length) return []
      const map = new Map(
        this.cboOptions.map(item => [String(item.CodCbo), item.label]),
      )
      return this.selectedCbos.map(codigo => map.get(String(codigo)) || codigo)
    },

    cnaeOptions() {
      return (this.cnaesRaw || []).map(item => {
        const id = String(item.idcnae ?? item.Idcnae ?? item.IdCnae ?? item.id ?? '')
        const desc = String(item.Descricao ?? item.descricao ?? item.desc_subclasse ?? '')
        return {
          ...item,
          idcnae: id,
          label: desc ? `${id} — ${desc}` : id,
        }
      }).filter(item => item.idcnae)
    },

    cnaeTodosSelecionado() {
      return this.selectedCnaes.length === 1 && String(this.selectedCnaes[0]) === '*'
    },

    cnaesSelecionadosLabels() {
      if (this.cnaeTodosSelecionado) return ['Todos']
      if (!this.selectedCnaes.length) return []
      const map = new Map(
        this.cnaeOptions.map(item => [String(item.idcnae), item.label]),
      )
      return this.selectedCnaes.map(id => map.get(String(id)) || id)
    },

    porteOptions() {
      return (this.portesRaw || []).map(item => {
        const id = String(
          item.IdPorteEmpresa ?? item.idporteEmpresa ?? item.idporte ?? item.id ?? '',
        )
        const desc = String(item.Descricao ?? item.descricao ?? '')
        return {
          ...item,
          IdPorteEmpresa: id,
          label: desc || id,
        }
      }).filter(item => item.IdPorteEmpresa)
    },

    porteTodosSelecionado() {
      return this.selectedPortes.length === 1 && String(this.selectedPortes[0]) === '*'
    },

    portesSelecionadosLabels() {
      if (this.porteTodosSelecionado) return ['Todos']
      if (!this.selectedPortes.length) return []
      const map = new Map(
        this.porteOptions.map(item => [String(item.IdPorteEmpresa), item.label]),
      )
      return this.selectedPortes.map(id => map.get(String(id)) || id)
    },

    naturezaOptions() {
      return (this.naturezasRaw || []).map(item => {
        const id = String(item.IdNaturezaJuridica ?? item.idNaturezaJuridica ?? item.id ?? '')
        const desc = String(item.descricao ?? item.Descricao ?? '')
        return {
          ...item,
          IdNaturezaJuridica: id,
          label: desc || id,
        }
      }).filter(item => item.IdNaturezaJuridica)
    },

    naturezaTodosSelecionado() {
      return this.selectedNaturezas.length === 1 && String(this.selectedNaturezas[0]) === '*'
    },

    naturezasSelecionadasLabels() {
      if (this.naturezaTodosSelecionado) return ['Todos']
      if (!this.selectedNaturezas.length) return []
      const map = new Map(
        this.naturezaOptions.map(item => [String(item.IdNaturezaJuridica), item.label]),
      )
      return this.selectedNaturezas.map(id => map.get(String(id)) || id)
    },

    situacaoOptions() {
      return (this.situacoesRaw || []).map(item => {
        const id = String(item.IdSituacaoEmpresa ?? item.idSituacaoEmpresa ?? item.id ?? '')
        const desc = String(item.descricao ?? item.Descricao ?? '')
        return {
          ...item,
          IdSituacaoEmpresa: id,
          label: desc || id,
        }
      }).filter(item => item.IdSituacaoEmpresa)
    },

    situacaoTodosSelecionado() {
      return this.selectedSituacoes.length === 1 && String(this.selectedSituacoes[0]) === '*'
    },

    situacoesSelecionadasLabels() {
      if (this.situacaoTodosSelecionado) return ['Todos']
      if (!this.selectedSituacoes.length) return []
      const map = new Map(
        this.situacaoOptions.map(item => [String(item.IdSituacaoEmpresa), item.label]),
      )
      return this.selectedSituacoes.map(id => map.get(String(id)) || id)
    },

    reviewRows() {
      const rows = [
        { label: 'Tipo', value: this.tipo === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica' },
        { label: 'Nome', value: this.nome || '—' },
        { label: 'Canais', value: this.canais.length ? this.canais.join(', ') : '—' },
        {
          label: 'Estados',
          value: this.selectedEstados.length ? this.selectedEstados.join(', ') : '—',
        },
        {
          label: 'Cidades',
          value: this.cidadesSelecionadasLabels.length
            ? this.cidadesSelecionadasLabels.join(', ')
            : '—',
        },
      ]

      if (this.tipo === 'pf') {
        rows.push(
          { label: 'Gênero', value: this.form.genero || '—' },
          { label: 'Idade', value: `${this.form.faixaetariaini ?? '—'} - ${this.form.faixaetariafim ?? '—'}` },
          {
            label: 'CBO',
            value: this.cbosSelecionadosLabels.length
              ? this.cbosSelecionadosLabels.join(', ')
              : '—',
          },
          {
            label: 'Renda',
            value: `${this.form.rendaInicial || '—'} - ${this.form.rendaFinal || '—'}`,
          },
        )
      } else {
        rows.push(
          {
            label: 'CNAE',
            value: this.cnaesSelecionadosLabels.length
              ? this.cnaesSelecionadosLabels.join(', ')
              : '—',
          },
          {
            label: 'Porte',
            value: this.portesSelecionadosLabels.length
              ? this.portesSelecionadosLabels.join(', ')
              : '—',
          },
          {
            label: 'Natureza',
            value: this.naturezasSelecionadasLabels.length
              ? this.naturezasSelecionadasLabels.join(', ')
              : '—',
          },
          {
            label: 'Situação',
            value: this.situacoesSelecionadasLabels.length
              ? this.situacoesSelecionadasLabels.join(', ')
              : '—',
          },
          { label: 'Funcionários', value: `${this.form.qtdFuncionariosInicial || '—'} - ${this.form.qtdFuncionariosFinal || '—'}` },
          { label: 'Faturamento', value: `${this.form.faturamentoInicial || '—'} - ${this.form.faturamentoFinal || '—'}` },
          { label: 'Com sócios', value: this.form.comsocios ? 'Sim' : 'Não' },
        )
      }

      return rows
    },
  },

  watch: {
    activeTab(tab) {
      if (tab === 1) this.loadLista()
    },
    listaTipo() {
      if (this.activeTab === 1) this.loadLista()
    },
    tipo() {
      if (this.currentStep > this.totalSteps) {
        this.currentStep = this.totalSteps
      }
    },
    currentStep(step) {
      if (step === 2) this.ensureCidadesLoaded()
      if (step === 4 && this.tipo === 'pf') this.ensureCbosLoaded()
      if (step === 3 && this.tipo === 'pj') this.ensurePjCombosLoaded()
    },
  },

  methods: {
    extractErrorMessage(error) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.menssage ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Falha na operação de prospecção.'
      )
    },

    extractResults(data) {
      if (Array.isArray(data?.results)) return data.results
      if (Array.isArray(data)) return data
      return []
    },

    joinOrTodos(selected, todosSelecionado) {
      if (todosSelecionado) return '*'
      if (!selected.length) return ''
      return selected.join(';')
    },

    filterCidades(item, queryText, itemText) {
      const text = String(itemText || '').toLowerCase()
      const query = String(queryText || '').toLowerCase()
      return text.indexOf(query) > -1
    },

    filterCbos(item, queryText, itemText) {
      const text = String(itemText || item.label || '').toLowerCase()
      const query = String(queryText || '').toLowerCase()
      return text.indexOf(query) > -1
    },

    filterCnaes(item, queryText, itemText) {
      const text = String(itemText || item.label || '').toLowerCase()
      const query = String(queryText || '').toLowerCase()
      return text.indexOf(query) > -1
    },

    async ensureCidadesLoaded() {
      if (this.cidadesRaw.length || this.loadingCidades) return
      this.loadingCidades = true
      try {
        const data = await getCidadesProspeccao()
        this.cidadesRaw = Array.isArray(data?.cidades)
          ? data.cidades
          : (Array.isArray(data) ? data : [])
      } catch (error) {
        this.cidadesRaw = []
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.loadingCidades = false
      }
    },

    async ensureCbosLoaded() {
      if (this.cbosRaw.length || this.loadingCbos) return
      this.loadingCbos = true
      try {
        const data = await getCbosProspeccao()
        this.cbosRaw = this.extractResults(data)
      } catch (error) {
        this.cbosRaw = []
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.loadingCbos = false
      }
    },

    async ensurePjCombosLoaded() {
      await Promise.all([
        this.ensureCnaesLoaded(),
        this.ensurePortesLoaded(),
        this.ensureNaturezasLoaded(),
        this.ensureSituacoesLoaded(),
      ])
    },

    async ensureCnaesLoaded() {
      if (this.cnaesRaw.length || this.loadingCnaes) return
      this.loadingCnaes = true
      try {
        const data = await getCnaesProspeccao()
        this.cnaesRaw = this.extractResults(data)
      } catch (error) {
        this.cnaesRaw = []
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.loadingCnaes = false
      }
    },

    async ensurePortesLoaded() {
      if (this.portesRaw.length || this.loadingPortes) return
      this.loadingPortes = true
      try {
        const data = await getPortesProspeccao()
        this.portesRaw = this.extractResults(data)
      } catch (error) {
        this.portesRaw = []
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.loadingPortes = false
      }
    },

    async ensureNaturezasLoaded() {
      if (this.naturezasRaw.length || this.loadingNaturezas) return
      this.loadingNaturezas = true
      try {
        const data = await getNaturezasJuridicasProspeccao()
        this.naturezasRaw = this.extractResults(data)
      } catch (error) {
        this.naturezasRaw = []
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.loadingNaturezas = false
      }
    },

    async ensureSituacoesLoaded() {
      if (this.situacoesRaw.length || this.loadingSituacoes) return
      this.loadingSituacoes = true
      try {
        const data = await getSituacoesEmpresaProspeccao()
        this.situacoesRaw = this.extractResults(data)
      } catch (error) {
        this.situacoesRaw = []
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.loadingSituacoes = false
      }
    },

    selecionarTodosCbos() {
      this.selectedCbos = ['Todos']
    },

    selecionarTodosCnaes() {
      this.selectedCnaes = ['*']
    },

    selecionarTodosPortes() {
      this.selectedPortes = ['*']
    },

    selecionarTodasNaturezas() {
      this.selectedNaturezas = ['*']
    },

    selecionarTodasSituacoes() {
      this.selectedSituacoes = ['*']
    },

    onEstadosChange() {
      if (!this.selectedCidades.length) return
      const allowed = new Set(
        this.cidadeOptionsFiltradas.map(item => String(item.codigo)),
      )
      this.selectedCidades = this.selectedCidades.filter(codigo =>
        allowed.has(String(codigo)),
      )
    },

    toggleCanal(value) {
      if (this.canais.includes(value)) {
        this.canais = this.canais.filter(item => item !== value)
      } else {
        this.canais = [...this.canais, value]
      }
    },

    reiniciarWizard() {
      this.currentStep = 1
      this.tipo = 'pf'
      this.nome = ''
      this.canais = ['celular']
      this.form = emptyForm()
      this.selectedEstados = []
      this.selectedCidades = []
      this.selectedCbos = []
      this.selectedCnaes = []
      this.selectedPortes = []
      this.selectedNaturezas = []
      this.selectedSituacoes = []
    },

    prevStep() {
      if (this.currentStep > 1) this.currentStep -= 1
    },

    nextStep() {
      if (this.currentStep === 1) {
        if (!this.tipo) {
          this.$notify({ type: 'error', text: 'Selecione PF ou PJ.' })
          return
        }
        if (!this.nome.trim()) {
          this.$notify({ type: 'error', text: 'Informe o nome da prospecção.' })
          return
        }
        if (!this.canais.length) {
          this.$notify({ type: 'error', text: 'Selecione ao menos um canal (celular, fixo ou e-mail).' })
          return
        }
      }

      if (this.currentStep === 3 && this.tipo === 'pj') {
        if (!this.selectedCnaes.length) {
          this.$notify({ type: 'error', text: 'Selecione ao menos um CNAE.' })
          return
        }
        if (!this.selectedPortes.length) {
          this.$notify({ type: 'error', text: 'Selecione ao menos um porte.' })
          return
        }
        if (!this.selectedNaturezas.length) {
          this.$notify({ type: 'error', text: 'Selecione ao menos uma natureza jurídica.' })
          return
        }
        if (!this.selectedSituacoes.length) {
          this.$notify({ type: 'error', text: 'Selecione ao menos uma situação.' })
          return
        }
      }

      if (this.currentStep < this.totalSteps) {
        this.currentStep += 1
      }
    },

    buildLocalizacaoPayload() {
      // Cidade → códigos; estado → UFs; o outro campo vai '*'
      if (this.selectedCidades.length) {
        return {
          cidades: this.selectedCidades.join(';'),
          estados: '*',
        }
      }

      if (this.selectedEstados.length) {
        return {
          cidades: '*',
          estados: this.selectedEstados
            .map(uf => String(uf).toLowerCase())
            .join(';'),
        }
      }

      return {
        cidades: '*',
        estados: '*',
      }
    },

    buildPayload() {
      const localizacao = this.buildLocalizacaoPayload()
      const base = {
        nomeprospecao: this.nome.trim(),
        ...localizacao,
        celular: this.canais.includes('celular') ? 1 : 0,
        fixo: this.canais.includes('fixo') ? 1 : 0,
        email: this.canais.includes('email') ? 1 : 0,
        statusprospecao: 1,
        totalregistrosaprovado: 0,
      }

      if (this.tipo === 'pf') {
        const cbo = this.cboTodosSelecionado
          ? 'Todos'
          : this.selectedCbos.join(';')

        return {
          ...base,
          faixaetariaini: this.form.faixaetariaini,
          faixaetariafim: this.form.faixaetariafim,
          genero: this.form.genero,
          cbo,
          rendaInicial: String(this.form.rendaInicial || '')
            .replace('R$', '')
            .replace(/\s/g, '')
            .replace(',', '.'),
          rendaFinal: String(this.form.rendaFinal || '')
            .replace('R$', '')
            .replace(/\s/g, '')
            .replace(',', '.'),
        }
      }

      return {
        ...base,
        tipoCanalOutro: 0,
        naturezajuridica: this.joinOrTodos(
          this.selectedNaturezas,
          this.naturezaTodosSelecionado,
        ) || '*',
        porteempresa: this.joinOrTodos(
          this.selectedPortes,
          this.porteTodosSelecionado,
        ) || '*',
        situacaoempresa: this.joinOrTodos(
          this.selectedSituacoes,
          this.situacaoTodosSelecionado,
        ) || '*',
        tipoempresa: String(this.form.tipoempresa || '').trim() || '*',
        comsocios: this.form.comsocios,
        qtdFuncionariosInicial: this.form.qtdFuncionariosInicial,
        qtdFuncionariosFinal: this.form.qtdFuncionariosFinal,
        faturamentoInicial: this.form.faturamentoInicial,
        faturamentoFinal: this.form.faturamentoFinal,
        tempoEmpresaEmMesesIni: this.form.tempoEmpresaEmMesesIni,
        tempoEmpresaEmMesesFim: this.form.tempoEmpresaEmMesesFim,
        capitalSocialInicial: this.form.capitalSocialInicial,
        capitalSocialFinal: this.form.capitalSocialFinal,
        cnae: this.joinOrTodos(this.selectedCnaes, this.cnaeTodosSelecionado) || '*',
      }
    },

    async enviar() {
      this.sending = true
      this.$loading(true)
      try {
        const payload = this.buildPayload()
        console.log('[Prospecção] payload:', this.tipo, payload)
        const data =
          this.tipo === 'pj'
            ? await enviarProspeccaoPj(payload)
            : await enviarProspeccaoPf(payload)
        console.log('[Prospecção] retorno:', data)
        this.$notify({ type: 'success', text: 'Prospecção enviada com sucesso.' })
        this.listaTipo = this.tipo
        this.reiniciarWizard()
        this.activeTab = 1
        await this.loadLista()
      } catch (error) {
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.sending = false
        this.$loading(false)
      }
    },

    async loadLista() {
      this.loadingLista = true
      try {
        const body = { ultimosDias: this.ultimosDias || 7 }
        const data =
          this.listaTipo === 'pj'
            ? await listarProspeccoesPj(body)
            : await listarProspeccoesPf(body)
        this.lista = normalizeLista(data)
      } catch (error) {
        this.lista = []
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.loadingLista = false
      }
    },

    async verContagem(item) {
      this.actionId = item.id
      this.actionType = 'contagem'
      this.$loading(true)
      try {
        const tipo = this.listaTipo === 'pj' ? 2 : 1
        const body = { idProspeccao: item.id, tipo }
        const data =
          this.listaTipo === 'pj'
            ? await contagemProspeccaoPj(body)
            : await contagemProspeccaoPf(body)
        this.detailTitle = `Contagem #${item.id}`
        this.detailData = data
        this.detailDialog = true
      } catch (error) {
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.actionId = null
        this.actionType = ''
        this.$loading(false)
      }
    },

    openAprovar(item) {
      this.aprovarItem = item
      this.qtdAprovado = Number(item.total) > 0 ? Number(item.total) : 1
      this.aprovarDialog = true
    },

    async confirmarAprovar() {
      if (!this.aprovarItem?.id) return
      this.actionId = this.aprovarItem.id
      this.actionType = 'aprovar'
      this.$loading(true)
      try {
        const tipo = this.listaTipo === 'pj' ? 2 : 1
        const body = {
          idProspeccao: this.aprovarItem.id,
          tipo,
          qtdAprovado: this.qtdAprovado || 1,
        }
        if (this.listaTipo === 'pj') {
          await aprovarProspeccaoPj(body)
        } else {
          await aprovarProspeccaoPf(body)
        }
        this.$notify({ type: 'success', text: 'Prospecção aprovada.' })
        this.aprovarDialog = false
        await this.loadLista()
      } catch (error) {
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.actionId = null
        this.actionType = ''
        this.$loading(false)
      }
    },

    async recusar(item) {
      this.actionId = item.id
      this.actionType = 'recusar'
      this.$loading(true)
      try {
        const tipo = this.listaTipo === 'pj' ? 2 : 1
        const body = { idProspeccao: item.id, tipo }
        if (this.listaTipo === 'pj') {
          await recusarProspeccaoPj(body)
        } else {
          await recusarProspeccaoPf(body)
        }
        this.$notify({ type: 'success', text: 'Prospecção recusada.' })
        await this.loadLista()
      } catch (error) {
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.actionId = null
        this.actionType = ''
        this.$loading(false)
      }
    },

    async verRetorno(item) {
      this.actionId = item.id
      this.actionType = 'retorno'
      this.$loading(true)
      try {
        const data =
          this.listaTipo === 'pj'
            ? await retornoProspeccaoPj(item.id)
            : await retornoProspeccaoPf(item.id)
        this.detailTitle = `Retorno #${item.id}`
        this.detailData = data
        this.detailDialog = true
      } catch (error) {
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.actionId = null
        this.actionType = ''
        this.$loading(false)
      }
    },

    async verDownload(item) {
      this.actionId = item.id
      this.actionType = 'download'
      this.$loading(true)
      try {
        const data = await downloadProspeccao(item.id)
        this.detailTitle = `Download #${item.id}`
        this.detailData = data
        this.detailDialog = true
      } catch (error) {
        this.$notify({ type: 'error', text: this.extractErrorMessage(error) })
      } finally {
        this.actionId = null
        this.actionType = ''
        this.$loading(false)
      }
    },
  },
}
</script>

<style scoped>
.prospeccao-tabs-items {
  background-color: transparent !important;
}

.prospeccao-tabs-items >>> .v-window__container,
.prospeccao-tabs-items >>> .v-window-item {
  background-color: transparent !important;
}

.prosp-avatar {
  background: rgba(145, 85, 253, 0.12);
}

.prosp-avatar--selected {
  background: rgba(145, 85, 253, 0.28) !important;
  color: #9155fd !important;
}

.option-card {
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.option-card:hover {
  border-color: #9155fd !important;
  background-color: rgba(145, 85, 253, 0.06) !important;
}

.option-card--selected {
  border-color: #9155fd !important;
  background-color: rgba(145, 85, 253, 0.12) !important;
}

.theme--dark .option-card {
  border-color: rgba(255, 255, 255, 0.22) !important;
}

.theme--dark .option-card:hover {
  border-color: #b794ff !important;
  background-color: rgba(145, 85, 253, 0.18) !important;
}

.theme--dark .option-card--selected {
  border-color: #b794ff !important;
  background-color: rgba(145, 85, 253, 0.28) !important;
}

.theme--dark .prosp-avatar {
  background: rgba(145, 85, 253, 0.2);
}

.theme--dark .prosp-avatar--selected {
  background: rgba(145, 85, 253, 0.4) !important;
  color: #b794ff !important;
}

.prosp-tipo-chip {
  background-color: transparent !important;
  font-weight: 600;
}

.prosp-tipo-chip--pf {
  border-color: #4caf50 !important;
  color: #4caf50 !important;
}

.prosp-tipo-chip--pj {
  border-color: #9155fd !important;
  color: #9155fd !important;
}

.theme--dark .prosp-tipo-chip--pf {
  border-color: #81c784 !important;
  color: #81c784 !important;
}

.theme--dark .prosp-tipo-chip--pj {
  border-color: #b794ff !important;
  color: #b794ff !important;
}
</style>
